import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { backendURL } from "../config";
import {
    Shield, ArrowLeft, CheckCircle2, Clock, Terminal, Globe, ExternalLink,
    AlertTriangle, ShieldX, ShieldCheck, RefreshCw, Send, Loader2, Sparkles,
    FileText, Layers, Activity, Server, Lock, User, ChevronRight, Hash,
    Database, Cpu, Share2, Download, Filter, CornerDownLeft, Bot, MessageSquare
} from "lucide-react";

export default function ThreatIntelReportDetail() {
    const { reportId } = useParams();
    const navigate = useNavigate();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rehunting, setRehunting] = useState(false);
    const [aiQuestion, setAiQuestion] = useState("");
    const [aiAnswers, setAiAnswers] = useState([]);
    const [askingAi, setAskingAi] = useState(false);
    const [filterSeverity, setFilterSeverity] = useState("all");
    const chatBottomRef = useRef(null);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        if (chatBottomRef.current) {
            chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [aiAnswers, askingAi]);

    useEffect(() => {
        fetchReportDetail();
    }, [reportId]);

    const fetchReportDetail = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/threat-intel/reports/${reportId}`, { headers });
            setReport(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load report detail:", err);
        }
        setLoading(false);
    };

    const handleRehunt = async () => {
        setRehunting(true);
        try {
            await axios.post(`${backendURL}/api/threat-intel/hunt/${reportId}`, {}, { headers });
            await fetchReportDetail();
        } catch (err) {
            console.error("Rehunt failed:", err);
        }
        setRehunting(false);
    };

    const handleAskAi = async (e) => {
        e.preventDefault();
        if (!aiQuestion.trim() || askingAi) return;

        const q = aiQuestion;
        setAiQuestion("");
        setAskingAi(true);
        try {
            const res = await axios.post(`${backendURL}/api/threat-intel/reports/${reportId}/ask`, { question: q }, { headers });
            setAiAnswers(prev => [...prev, { question: q, answer: res.data.answer, ts: new Date() }]);
        } catch (err) {
            console.error("AI Ask failed:", err);
            setAiAnswers(prev => [...prev, { question: q, answer: "AI analysis failed to respond. Please try again.", ts: new Date() }]);
        }
        setAskingAi(false);
    };

    if (loading) {
        return (
            <div className="w-full min-h-[600px] flex flex-col items-center justify-center space-y-3">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                <p className="text-slate-400 text-[13px] font-mono">Loading QueryTel Intel to Impact Workbench...</p>
            </div>
        );
    }

    if (!report) {
        return (
            <div className="w-full p-8 text-center space-y-4">
                <ShieldX className="w-12 h-12 text-rose-500 mx-auto" />
                <h2 className="text-xl font-bold text-white">Threat Intelligence Report Not Found</h2>
                <button
                    onClick={() => navigate("/threat-intel")}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-[12px] font-semibold"
                >
                    Back to Threat Intel Reports
                </button>
            </div>
        );
    }

    const filteredFindings = (report.findings || []).filter(f => {
        if (filterSeverity === "all") return true;
        return f.severity?.toLowerCase() === filterSeverity.toLowerCase();
    });

    return (
        <div className="w-full space-y-4 pb-12">
            {/* ─── Breadcrumb & Top Bar ─── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#111827] border border-white/[0.06] rounded-xl px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400 overflow-x-auto whitespace-nowrap">
                    <Link to="/threat-intel" className="hover:text-indigo-400 transition-colors">Hunt</Link>
                    <span>/</span>
                    <Link to="/threat-intel" className="hover:text-indigo-400 transition-colors">Threat Hunting</Link>
                    <span>/</span>
                    <span className="text-white font-bold">{report.reportId} — {report.title}</span>
                </div>

                <div className="flex items-center gap-2 self-start md:self-auto flex-wrap">
                    {/* Verdict Pill */}
                    <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase border ${
                        report.verdict === "Malicious" ? "bg-rose-500/20 border-rose-500/30 text-rose-300" :
                        report.verdict === "Suspicious" ? "bg-amber-500/20 border-amber-500/30 text-amber-300" :
                        "bg-emerald-500/20 border-emerald-500/30 text-emerald-300"
                    }`}>
                        Keep Verdict: {report.verdict}
                    </span>

                    {/* Entities Pill */}
                    <span className="px-2.5 py-1 bg-white/[0.04] border border-white/[0.08] text-slate-300 rounded-md text-[11px] font-mono">
                        {report.entitiesCount ?? (report.entities?.length || 0)} Entities
                    </span>

                    {/* Findings Pill */}
                    <span className="px-2.5 py-1 bg-white/[0.04] border border-white/[0.08] text-slate-300 rounded-md text-[11px] font-mono">
                        {report.findingsCount ?? (report.findings?.length || 0)} Findings
                    </span>

                    {/* Actions Button */}
                    <button
                        onClick={handleRehunt}
                        disabled={rehunting}
                        className="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-orange-600 to-indigo-600 hover:from-orange-500 hover:to-indigo-500 text-white rounded-md text-[11px] font-bold shadow transition-all disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3 h-3 ${rehunting ? "animate-spin" : ""}`} />
                        {rehunting ? "Hunting..." : "Actions (1)"}
                    </button>

                    <button
                        onClick={() => navigate("/threat-intel")}
                        className="flex items-center gap-1 px-3 py-1 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-md text-[11px] font-semibold transition-colors"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back
                    </button>
                </div>
            </div>

            {/* ─── 3-Panel QueryTel "Intel to Impact" Canvas ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start min-h-[750px]">
                
                {/* ═══ 1. LEFT PANEL: Autonomous Hunt Plan (3 cols) ═══ */}
                <div className="lg:col-span-3 bg-[#111827] border border-white/[0.06] rounded-xl flex flex-col h-[750px] shadow-sm">
                    <div className="p-3.5 border-b border-white/[0.06] flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            <h3 className="text-[12px] font-bold text-white uppercase tracking-wider">Plan</h3>
                        </div>
                        <span className="text-[10px] font-mono text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                            {(report.huntPlan || []).length}/{(report.huntPlan || []).length} Completed
                        </span>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2.5 custom-scrollbar">
                        {(report.huntPlan || []).map((step, idx) => (
                            <div
                                key={step.id || idx}
                                className="p-2.5 bg-black/20 hover:bg-white/[0.02] border border-white/[0.04] rounded-lg transition-all space-y-1.5"
                            >
                                <div className="flex items-start gap-2">
                                    <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <CheckCircle2 className="w-3 h-3" />
                                    </div>
                                    <p className="text-[11px] text-slate-200 leading-snug font-medium line-clamp-3">
                                        {step.title}
                                    </p>
                                </div>
                                <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono pl-6">
                                    <span className="text-slate-400">{step.category}</span>
                                    <span className="text-emerald-400/80">{step.duration || "45ms"}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ═══ 2. CENTER PANEL: Threat Intelligence Brief (5 cols) ═══ */}
                <div className="lg:col-span-5 bg-[#111827] border border-white/[0.06] rounded-xl flex flex-col h-[750px] shadow-sm">
                    {/* Timestamp Tab Header */}
                    <div className="p-3.5 border-b border-white/[0.06] flex items-center justify-between text-[11px] font-mono text-slate-400">
                        <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-indigo-400" />
                            <span>{report.publishedFull || "May 14, 2026, 12:45:00 AM EDT"}</span>
                        </div>
                        <span className="text-slate-500">CTI Ingestion Feed</span>
                    </div>

                    {/* Structured Document Content */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar text-[12px] text-slate-300">
                        {/* Title & Metadata */}
                        <div className="space-y-2 border-b border-white/[0.06] pb-4">
                            <h2 className="text-[16px] font-extrabold text-white leading-snug">
                                {report.title}
                            </h2>
                            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                <span className="text-indigo-400 font-semibold">{report.source}</span>
                                <span className="text-slate-600">•</span>
                                <span className="text-slate-400 font-mono">Actor: <strong className="text-slate-200">{report.actor || "Unassigned"}</strong></span>
                                <span className="text-slate-600">•</span>
                                <span className="text-rose-400 font-bold bg-rose-500/10 px-2 py-0.5 rounded border border-rose-500/20 font-mono">
                                    CVSS {report.cvssScore || "9.8"} Critical
                                </span>
                            </div>
                        </div>

                        {/* Executive Summary */}
                        <div className="space-y-1.5">
                            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Executive Overview</h4>
                            <p className="text-slate-300 leading-relaxed bg-black/20 p-3 rounded-lg border border-white/[0.04]">
                                {report.description}
                            </p>
                        </div>

                        {/* Attack Vectors & Technical Scope */}
                        <div className="space-y-2">
                            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Target Profile & Scope</h4>
                            <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
                                <div className="p-2.5 bg-black/30 rounded border border-white/[0.04]">
                                    <span className="text-slate-500 block text-[10px]">TARGET SECTOR</span>
                                    <span className="text-slate-200 font-semibold">{report.targetIndustry || "Cross-Industry"}</span>
                                </div>
                                <div className="p-2.5 bg-black/30 rounded border border-white/[0.04]">
                                    <span className="text-slate-500 block text-[10px]">TARGET REGION</span>
                                    <span className="text-slate-200 font-semibold">{report.targetRegion || "Global"}</span>
                                </div>
                            </div>
                        </div>

                        {/* MITRE ATT&CK Matrix */}
                        {report.mitreTechniques && report.mitreTechniques.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">MITRE ATT&CK Techniques</h4>
                                <div className="flex flex-wrap gap-1.5">
                                    {report.mitreTechniques.map((tech, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-300 rounded text-[11px] font-mono">
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Mitigation Guidance */}
                        <div className="space-y-2">
                            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Recommended Mitigation</h4>
                            <ul className="list-disc list-inside space-y-1 text-slate-300 text-[11.5px] leading-relaxed">
                                {report.iocs?.cves?.length > 0 ? (
                                    <li>Apply emergency vendor patch release or virtual patching signature on FortiGate perimeter.</li>
                                ) : (
                                    <li>Enforce perimeter firewall drop rules and blackhole routing for active C2 botnet IPs.</li>
                                )}
                                <li>
                                    Isolate any host communicating with observed external indicators:{" "}
                                    <strong className="font-mono text-indigo-400">
                                        {report.iocs?.ips?.length > 0
                                            ? report.iocs.ips.join(", ")
                                            : report.iocs?.domains?.length > 0
                                            ? report.iocs.domains.join(", ")
                                            : "Inbound threat probes (Dynamic FortiGate Drop Active)"}
                                    </strong>.
                                </li>
                                <li>Invalidate session tokens and force credential rotation on any exposed endpoints.</li>
                            </ul>
                        </div>

                        {/* AI Conversation Q&A History */}
                        {(aiAnswers.length > 0 || askingAi) && (
                            <div className="space-y-4 pt-4 border-t border-white/[0.08]">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-[11px] font-bold text-indigo-400 uppercase tracking-wider flex items-center gap-1.5">
                                        <Sparkles className="w-3.5 h-3.5" />
                                        AI Analyst Interrogation History
                                    </h4>
                                    <span className="text-[10px] font-mono text-slate-500 bg-white/[0.03] px-2 py-0.5 rounded border border-white/[0.06]">
                                        {aiAnswers.length} {aiAnswers.length === 1 ? "Query" : "Queries"}
                                    </span>
                                </div>

                                <div className="space-y-3.5">
                                    {aiAnswers.map((item, idx) => (
                                        <div key={idx} className="space-y-2.5">
                                            {/* User Question Bubble */}
                                            <div className="flex items-start justify-end gap-2 pl-6">
                                                <div className="bg-indigo-600/90 text-white px-3.5 py-2 rounded-2xl rounded-tr-none text-[12px] font-medium shadow-sm max-w-[90%] leading-snug">
                                                    {item.question}
                                                </div>
                                                <div className="w-6 h-6 rounded-full bg-indigo-500/20 text-indigo-300 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5 border border-indigo-500/30">
                                                    <User className="w-3.5 h-3.5" />
                                                </div>
                                            </div>

                                            {/* AI Answer Bubble */}
                                            <div className="flex items-start gap-2.5 pr-2">
                                                <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5 shadow-[0_0_10px_rgba(99,102,241,0.3)]">
                                                    <Sparkles className="w-3.5 h-3.5" />
                                                </div>
                                                <div className="flex-1 bg-[#151c2c] border border-indigo-500/20 rounded-2xl rounded-tl-none p-3.5 text-[12px] shadow-sm space-y-2">
                                                    <div className="flex items-center justify-between pb-1.5 border-b border-white/[0.06] text-[10px] text-slate-400 font-mono">
                                                        <span className="text-indigo-300 font-semibold flex items-center gap-1">
                                                            QueryTel SOC AI Analyst
                                                        </span>
                                                        <span>{item.ts ? new Date(item.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Just now"}</span>
                                                    </div>
                                                    <div className="ai-markdown-content text-slate-200 leading-relaxed">
                                                        <ReactMarkdown
                                                            components={{
                                                                h3: ({ children }) => <h3 className="text-[12.5px] font-bold text-white mt-2 mb-1 text-indigo-200">{children}</h3>,
                                                                h4: ({ children }) => <h4 className="text-[11.5px] font-bold text-indigo-300 uppercase tracking-wider mt-2.5 mb-1 pb-0.5 border-b border-white/[0.04]">{children}</h4>,
                                                                p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed text-slate-300">{children}</p>,
                                                                strong: ({ children }) => <strong className="font-semibold text-slate-100">{children}</strong>,
                                                                ul: ({ children }) => <ul className="my-1.5 pl-4 list-disc space-y-1 text-slate-300">{children}</ul>,
                                                                ol: ({ children }) => <ol className="my-1.5 pl-4 list-decimal space-y-1 text-slate-300">{children}</ol>,
                                                                li: ({ children }) => <li className="text-slate-300 leading-relaxed">{children}</li>,
                                                                code: ({ children }) => (
                                                                    <code className="px-1.5 py-0.5 rounded bg-indigo-950/80 text-indigo-300 font-mono text-[11px] border border-indigo-800/40">
                                                                        {children}
                                                                    </code>
                                                                )
                                                            }}
                                                        >
                                                            {item.answer}
                                                        </ReactMarkdown>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* AI Typing / Loading Indicator */}
                                    {askingAi && (
                                        <div className="flex items-start gap-2.5 pr-2">
                                            <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                                                <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                                            </div>
                                            <div className="bg-[#151c2c] border border-indigo-500/20 rounded-2xl rounded-tl-none px-4 py-2.5 flex items-center gap-2 text-slate-400 text-[11.5px] font-mono">
                                                <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                                                <span>Analyzing telemetry & threat intelligence...</span>
                                            </div>
                                        </div>
                                    )}

                                    <div ref={chatBottomRef} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Bottom AI Interrogation Input */}
                    <form onSubmit={handleAskAi} className="p-3 border-t border-white/[0.06] bg-black/40 flex items-center gap-2">
                        <input
                            type="text"
                            placeholder="Ask QueryTel about this threat or query 636k logs..."
                            value={aiQuestion}
                            onChange={(e) => setAiQuestion(e.target.value)}
                            disabled={askingAi}
                            className="flex-1 bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-slate-500 outline-none focus:border-indigo-500"
                        />
                        <button
                            type="submit"
                            disabled={askingAi || !aiQuestion.trim()}
                            className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-[12px] font-bold transition-all disabled:opacity-40 flex items-center gap-1"
                        >
                            {askingAi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                    </form>
                </div>

                {/* ═══ 3. RIGHT PANEL: Confirmed Environment Findings (4 cols) ═══ */}
                <div className="lg:col-span-4 bg-[#111827] border border-white/[0.06] rounded-xl flex flex-col h-[750px] shadow-sm">
                    {/* Header & Filter Pills */}
                    <div className="p-3.5 border-b border-white/[0.06] flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4 text-rose-400" />
                            <h3 className="text-[12px] font-bold text-white uppercase tracking-wider">Hits & Evidence</h3>
                        </div>
                        
                        <div className="flex items-center bg-black/30 border border-white/[0.08] rounded-md p-0.5 text-[10px]">
                            {["all", "Critical", "High"].map(s => (
                                <button
                                    key={s}
                                    onClick={() => setFilterSeverity(s)}
                                    className={`px-2 py-0.5 rounded capitalize font-mono transition-all ${
                                        filterSeverity === s ? "bg-rose-600 text-white font-bold" : "text-slate-400 hover:text-white"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Scrollable Findings Cards */}
                    <div className="flex-1 overflow-y-auto p-3.5 space-y-3 custom-scrollbar">
                        {filteredFindings.length === 0 ? (
                            <div className="py-16 text-center text-slate-500 space-y-2">
                                <ShieldCheck className="w-10 h-10 mx-auto text-emerald-400/50" />
                                <p className="text-[12px]">No findings match severity filter.</p>
                            </div>
                        ) : (
                            filteredFindings.map((finding, idx) => {
                                const isCritical = finding.severity === "Critical";
                                const isHigh = finding.severity === "High";

                                return (
                                    <div
                                        key={finding.id || idx}
                                        className={`p-3.5 rounded-xl border transition-all space-y-2.5 ${
                                            isCritical
                                                ? "bg-rose-500/[0.08] border-rose-500/25"
                                                : isHigh
                                                ? "bg-amber-500/[0.08] border-amber-500/25"
                                                : "bg-blue-500/[0.06] border-blue-500/20"
                                        }`}
                                    >
                                        {/* Severity Tag & Hit Count */}
                                        <div className="flex items-center justify-between">
                                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${
                                                isCritical ? "bg-rose-500/20 text-rose-300 border-rose-500/30" :
                                                isHigh ? "bg-amber-500/20 text-amber-300 border-amber-500/30" :
                                                "bg-blue-500/20 text-blue-300 border-blue-500/30"
                                            }`}>
                                                {finding.severity}
                                            </span>

                                            {finding.hitCount > 0 && (
                                                <span className="text-[10.5px] font-mono font-bold text-rose-400 bg-black/40 px-2 py-0.5 rounded border border-rose-500/20">
                                                    {finding.hitCount} matches
                                                </span>
                                            )}
                                        </div>

                                        {/* Finding Title */}
                                        <h4 className="text-[12.5px] font-bold text-white leading-snug">
                                            {finding.title}
                                        </h4>

                                        {/* Narrative Description */}
                                        <p className="text-[11.5px] text-slate-300 leading-relaxed">
                                            {finding.text}
                                        </p>

                                        {/* Affected Assets & Action */}
                                        {finding.affectedAssets && (
                                            <div className="pt-1.5 border-t border-white/[0.06] space-y-1 text-[10.5px] font-mono">
                                                <div className="flex items-center justify-between text-slate-400">
                                                    <span>AFFECTED HOSTS:</span>
                                                    <span className="text-slate-200 font-bold">{finding.affectedAssets.join(', ')}</span>
                                                </div>
                                                {finding.actionTaken && (
                                                    <div className="text-emerald-400 flex items-center gap-1 pt-0.5">
                                                        <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                                                        <span className="truncate">{finding.actionTaken}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
